/* -----------------------------------------------------------------------------
 * Backend API client.
 *
 * Base URL comes from REACT_APP_API_URL (see frontend/.env), defaulting to the
 * local backend. All calls return the parsed JSON body; non-2xx responses throw
 * an Error carrying the server's message so forms can show it directly.
 * -------------------------------------------------------------------------- */

const BASE = (process.env.REACT_APP_API_URL || 'http://localhost:4000').replace(/\/$/, '');

async function request(path, { method = 'GET', body, token } = {}) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'x-admin-token': token } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  let data = {};
  try {
    data = await res.json();
  } catch {
  }
  if (!res.ok || data.ok === false) {
    throw new Error(data.error || `Request failed (${res.status})`);
  }
  return data;
}

// ── public ──────────────────────────────────────────────────────────────
export const joinWaitlist = (email, opts = {}) =>
  request('/api/waitlist', { method: 'POST', body: { email, ...opts } });

export const subscribeNewsletter = (email, language) =>
  request('/api/newsletter', { method: 'POST', body: { email, language } });

export const askQuestion = (payload) =>
  request('/api/questions', { method: 'POST', body: payload });

/* ── Market Pulse: session cache + background prefetch ─────────────────────
 * Blog reads can be slow, so we cache each response in sessionStorage (lives
 * until the tab closes) and use stale-while-revalidate: a cached response is
 * returned instantly, while a fresh copy is fetched in the background to keep
 * the next navigation current. `prefetchMarketPulse` warms the whole blog in
 * the background once the app has loaded, so opening Market Pulse feels
 * instant even on the first visit within a tab.
 * ------------------------------------------------------------------------ */
const CACHE_NS = 'napho.pulse.';

function cacheGet(key) {
  try {
    const raw = sessionStorage.getItem(CACHE_NS + key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function cacheSet(key, value) {
  try {
    sessionStorage.setItem(CACHE_NS + key, JSON.stringify(value));
  } catch {
  }
}

async function cached(key, fetcher) {
  const hit = cacheGet(key);
  if (hit) {
    fetcher().then((fresh) => cacheSet(key, fresh)).catch(() => {});
    return hit;
  }
  const fresh = await fetcher();
  cacheSet(key, fresh);
  return fresh;
}

export const fetchPosts = (lang = 'en') =>
  cached(`list.${lang}`, () => request(`/api/posts?lang=${lang}`));
export const fetchLatestPost = (lang = 'en') =>
  cached(`latest.${lang}`, () => request(`/api/posts/latest?lang=${lang}`));
export const fetchPost = (slug, lang = 'en') =>
  cached(`post.${lang}.${slug}`, () => request(`/api/posts/${slug}?lang=${lang}`));

const prefetched = new Set();
export async function prefetchMarketPulse(lang = 'en') {
  if (prefetched.has(lang)) return;
  prefetched.add(lang);
  try {
    const [{ posts = [] }] = await Promise.all([fetchPosts(lang), fetchLatestPost(lang)]);
    await Promise.all(posts.slice(0, 8).map((p) => fetchPost(p.slug, lang).catch(() => {})));
  } catch {
    prefetched.delete(lang);
  }
}

// ── admin ───────────────────────────────────────────────────────────────
export const adminLogin = (token) => request('/api/admin/login', { method: 'POST', token });
export const adminListPosts = (token) => request('/api/admin/posts', { token });
export const adminCreatePost = (token, post) =>
  request('/api/admin/posts', { method: 'POST', token, body: post });
export const adminDeletePost = (token, slug) =>
  request(`/api/admin/posts/${slug}`, { method: 'DELETE', token });
export const adminSubmissions = (token) => request('/api/admin/submissions', { token });

export { BASE as API_BASE };
